import React, { useState } from "react"

// The addresses an email of the organizer's list goes to (ticket 16; Phase C2b): the account (sign-in) email ticked by default, the
// organizer's contact email offered, an address added for this email only (the account is not changed), 1 to 5 in all; "I checked that
// the list goes to the right address" is required for every email and is cleared by any change of the addresses. Used by the sending,
// "Send again" and the reminder, and by a publication email sent again (Phase E2p), which names what it checks (`checkLabel`). `value` is
// `{ addresses: [{ email, role, on }], checked }`.
export const MAX_RECIPIENTS = 5
// The address rule of the sending (sportya-api common/tournamentProposalList `listEmail`), so an address offered is one it accepts.
const EMAIL = /^[^\s@<>()[\],;:"]+@[^\s@<>()[\],;:"]+\.[^\s@<>()[\],;:"]+$/
const ROLES = { account: "Account email (sign-in)", contact: "Organizer contact email", added: "Added for this email only" }
export const initialAddresses = organizer => ({ addresses: (organizer?.emails || []).map(({ email, role }) => ({ email, role, on: role === "account" })),
  checked: false })
export const recipientsOf = value => value.addresses.filter(({ on }) => on).map(({ email }) => email)
// Something the SuperAdmin chose since the dialog opened, which closing it asks to discard.
export const addressesChanged = (value, organizer) => value.checked
  || JSON.stringify(value.addresses) !== JSON.stringify(initialAddresses(organizer).addresses)
export const addressesReady = value => recipientsOf(value).length > 0 && recipientsOf(value).length <= MAX_RECIPIENTS && value.checked

export default function AddressPicker({ id, value, team, onChange, checkLabel = "I checked that the list goes to the right address" }) {
  const [typed, setTyped] = useState("")
  const [error, setError] = useState(null)
  const chosen = recipientsOf(value).length
  // Every change of the addresses asks for the check again.
  const change = addresses => onChange({ addresses, checked: false })
  const add = () => {
    const email = typed.trim().toLowerCase()
    if (chosen >= MAX_RECIPIENTS) return setError(`At most ${MAX_RECIPIENTS} addresses: untick one first.`)
    if (!EMAIL.test(email) || email.length > 254) return setError("Not an email address.")
    if (value.addresses.some(address => address.email.toLowerCase() === email)) return setError("This address is already in the list.")
    setError(null)
    setTyped("")
    return change([...value.addresses, { email, role: "added", on: true }])
  }
  return <div className="ep-stack ep-gap">
    <span className="ep-label">To</span>
    <ul className="ep-recipients" aria-label="Addresses">
      {value.addresses.map((address, index) => <li key={address.email}>
        <input type="checkbox" id={`${id}-address-${index}`} checked={address.on} disabled={!address.on && chosen >= MAX_RECIPIENTS}
          onChange={() => change(value.addresses.map((item, at) => (at === index ? { ...item, on: !item.on } : item)))} />
        <label htmlFor={`${id}-address-${index}`} className="ep-stack"><b>{address.email}</b><span className="ep-meta">{ROLES[address.role] || address.role}</span></label>
        {address.role === "added" && <button type="button" className="ep-link" aria-label={`Remove ${address.email}`}
          onClick={() => change(value.addresses.filter((item, at) => at !== index))}>Remove</button>}
      </li>)}
    </ul>
    {!value.addresses.length && <span className="ep-meta">The organizer's account has no email address: add one for this email.</span>}
    <div className="ep-field">
      <label htmlFor={`${id}-added`}>Another address for this email</label>
      <span className="ep-inline">
        <input id={`${id}-added`} className="form-control" value={typed} placeholder="name@club.ro" maxLength={254}
          onChange={event => { setTyped(event.target.value); setError(null) }}
          onKeyDown={event => { if (event.key === "Enter") { event.preventDefault(); add() } }} />
        <button type="button" className="btn btn-light" disabled={!typed.trim() || chosen >= MAX_RECIPIENTS} onClick={add}>Add</button>
      </span>
      {error && <span className="ep-error-text" role="alert">{error}</span>}
    </div>
    <span className="ep-meta">{team?.email ? `Replies go to ${team.email}; the calendar team gets a hidden copy. ` : ""}The organizer's account is not changed. At most
      {` ${MAX_RECIPIENTS}`} addresses.</span>
    <label className="ep-check">
      <input type="checkbox" checked={value.checked} disabled={!chosen} onChange={() => onChange({ ...value, checked: !value.checked })} />
      <b>{checkLabel}</b>
    </label>
    {!chosen && <span className="ep-error-text">Choose at least one address.</span>}
  </div>
}
